
import { Injectable } from '@angular/core';
import { UserSettingsService, UserTableSetting } from '@root/src/app/core/services/user/user-settings.service';
import { UserSessionType } from '@root/src/app/core/model/user/session-type';
import { V2UserSetting } from '@bssh/ng-sdk';
import environment from '@root/src/environments/environment';
import * as _ from 'lodash';
import { GridOptions } from 'ag-grid-community';
import { CustomColDef } from '@bssh/comp-lib/lib/components/data-table/custom-colDef';
import { BsshDataTableComponent } from '@app/shared/bssh-data-table/bssh-data-table.component';

export interface IUserGridSettings {
    limit?: number
}

@Injectable({
    providedIn: 'root'
})

export class TableSettingsService {
    constructor(private userSettingsService: UserSettingsService) { }

    public generateSelectionColumns(
        columnDefinitions: CustomColDef[]
    ): any[] {
        const selectionColumns: any[] = [];
        columnDefinitions
            .filter(col => !col.hide)
            .forEach(col => {
                selectionColumns.push({ colId: col.colId, state: true });
            });
        return selectionColumns;
    }

    public getColumnSettings(settingName: UserTableSetting, columnSettings: IColumnSetting[]): any {
        const setting = this.userSettingsService.getUserSetting(UserSessionType.LoggedInUser, settingName);
        if (setting) {
            const columnSelections = JSON.parse(setting.Value as string);
            // Get the columns which are marked 'showAlways' and transform them to objects to match the API response.
            const columnsToShowAlways = _.mapValues(_.keyBy(columnSettings.filter(x => x.showAlways === true), 'Id'), x => {
                return {
                    isVisible: true
                };
            });
            return { ...columnSelections, ...columnsToShowAlways };
        }
    }

    public getGridSettings(settingName: UserTableSetting): IUserGridSettings {
        const setting = this.userSettingsService.getUserSetting(UserSessionType.LoggedInUser, settingName);
        if (setting) {
            return JSON.parse(setting.Value as string);
        } else {
            return {};
        }
    }

    public applyGridSettings(
        userSavedGridSettings: IUserGridSettings,
        gridOptions: GridOptions,
        dataTable?: BsshDataTableComponent
    ): void {
        if (userSavedGridSettings) {
            const { limit } = userSavedGridSettings;
            if(limit && gridOptions.api) {
              gridOptions.api.paginationSetPageSize(limit);
              if(dataTable) {
                dataTable.pageSizeController.updatePageStatus();
              }
            }
        }
    }

    public setGridSettings(settingName: string, gridSettings: IUserGridSettings): boolean {
        const setting: V2UserSetting = {
            Id: null,
            Href: environment.apiEndpoint + '/users/loggedin/settings',
            Name: settingName,
            Value: JSON.stringify(gridSettings),
            Type: 'string'
        };
        this.userSettingsService.setUserSettings(UserSessionType.LoggedInUser, [setting]);
        return true;

    }

    public setColumnSettings(settingName: UserTableSetting, columnSettings: IColumnSetting[], selectionColumns: any[]): boolean {
        const userSelection = this.getUserSelections(columnSettings, selectionColumns);
        if (userSelection) {
            const setting: V2UserSetting = {
                Id: null,
                Href: environment.apiEndpoint + '/users/loggedin/settings',
                Name: settingName,
                Value: JSON.stringify(userSelection),
                Type: 'string'
            };
            this.userSettingsService.setUserSettings(UserSessionType.LoggedInUser, [setting]);
            return true;
        }

    }

    public setGridColumnsVisibilityDefaults(
        columnSettings: IColumnSetting[],
        userSavedColumnSettings: any,
        gridOptions: GridOptions,
        columnDefinitions: CustomColDef[]
    ): void {
        if (userSavedColumnSettings) {
            let keyValuePairsColumn: string[] = [];
            keyValuePairsColumn = Object.keys(userSavedColumnSettings);

            columnDefinitions.forEach(col => { col.hide = true; });

            keyValuePairsColumn.forEach(key => {

                const columnSetting = columnSettings.find(x => x.Id === key);
                if (columnSetting) {
                    const col: CustomColDef = columnDefinitions.find(
                        (column: CustomColDef) => (column.colId === columnSetting.label));
                    if (col) {
                        col.hide = false;
                    }
                }
            });
            if (gridOptions.api) {
                gridOptions.api.setColumnDefs([]);
                gridOptions.api.setColumnDefs(columnDefinitions);
                gridOptions.api.sizeColumnsToFit();
            }
        }
    }

    private getUserSelections(columnSettings: IColumnSetting[], selectionColumns: any[]): any {
        const keyValuePairs: [string, IColumnState][] = [];
        selectionColumns.forEach(col => {
            const column = columnSettings.find(item => item.label === col.colId);
            if (column) {
                const state: IColumnState = {
                    isVisible: true
                };
                keyValuePairs.push([column.Id, state ? state : null]);
            }
        });
        return _.fromPairs(keyValuePairs);
    }
}

export interface IColumnState {
    isVisible: boolean;
}

export interface IColumnSetting {
    Id: string;
    label: string;
    showAlways?: boolean;
}
